// Chart.js plugins are registered globally in src/chartjs-init.ts
// and configured here via options.plugins.

import { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { useIntl } from "react-intl";

import { Chart as ChartJS, ChartData, ChartOptions, Interaction, Chart, Color, TooltipItem } from "chart.js";

import { useAsu } from "@framework/provider-asu";
import { useUser } from "@framework/provider-user";
import { IUser } from "@framework/types";

import {
  applySyncedCrosshair,
  clearSyncedCrosshair,
  ExternalTooltip,
  formatExternalTooltipHeaderText,
  hideExternalTooltip,
  Interpolate,
  type IChartWithCrosshair,
  type IExternalTooltipContent,
} from "../../../../../../../../components/common/chart-plugins";
import { downloadDiagram } from "../../../../../../../../utils/download-diagram";
import { lineColors } from "../../constants";
import { IChartDataSignal, IFaultFrequencyChartCursor } from "../../interfaces";
import { themeProps } from "../../../../../../../../../src/components/theme";
import { ChartZoomOverview } from "../chart-zoom-overview";
import { CHART_SELECTION_EVENTS, IChartZoomControlProps, applyZoomBounds, useChartZoom } from "../chart-zoom";
import { StyledWrapper, StyledChartContainer } from "./styled";
import { formatMeasurementNumber } from "../../../../utils/measurement-format";
import { getChartExportFileName, IChartExportControlProps } from "../chart-export";
import {
  appendMarkerBandTooltipContent,
  appendMarkerPowerTooltipContent,
  buildMarkerTooltipContent,
} from "./marker-band";
import { getSignalMarkerPassageXValues } from "./marker-points";
import { calcSpectraBandPowerInRange } from "./spectra-band-power";
import { formatSignalCascadeTooltipTitle, formatSignalMeasurementDate, resolveSignalXDisplayScale } from "./tooltip";

// @ts-ignore
Interaction.modes.index = Interpolate;

export interface ISyncedCursorAction {
  nativeX: number | null;
  datasetIndex: number;
  locked: boolean;
  markerNativeX: number | null;
  markerDatasetIndex: number | null;
}

export interface IMultilineChartSignal extends IChartZoomControlProps, IChartExportControlProps {
  data: IChartDataSignal[];
  chartTitle: string;
  showHarmonicCursors?: boolean;
  showSubHarmonicCursors?: boolean;
  showMarkerPoints?: boolean;
  displayCascade3D?: boolean;
  showCascadeHoverLine?: boolean;
  faultFrequencies?: IFaultFrequencyChartCursor[] | null;
  tooltipInHeader?: boolean;
  onHeaderTooltipChange?: (text: string | null) => void;
  enableKeyboardCursorNavigation?: boolean;
  markerMode?: boolean;
  showMarkerPower?: boolean;
  markerTooltipType?: "spectrum" | "signal";
  xAxisDisplayScale?: number;
  xAxisDisplayUnits?: string;
  xAxisDisplayUnitsCode?: number;
  frequencyProfileNativeX?: number | null;
  onNativeXClick?: (nativeX: number, datasetIndex: number) => void;
  cursorLineColor?: string;
  syncCursorValues?: readonly (number | null)[] | null;
  syncMarkerValues?: readonly (number | null)[] | null;
  syncCursorDatasetIndex?: number | null;
  syncMarkerDatasetIndex?: number | null;
  onSyncedCursorAction?: (action: ISyncedCursorAction) => void;
}

const getFontFamily = () =>
  typeof themeProps?.options?.typography === "function"
    ? "Roboto"
    : themeProps?.options?.typography?.fontFamily ?? "Roboto";

export const MultilineChartSignal: FC<IMultilineChartSignal> = props => {
  const {
    data,
    chartTitle,
    showHarmonicCursors = false,
    showSubHarmonicCursors = false,
    showMarkerPoints = false,
    displayCascade3D = true,
    showCascadeHoverLine = true,
    faultFrequencies = null,
    registerReset,
    registerExport,
    onZoomStateChange,
    tooltipInHeader = false,
    onHeaderTooltipChange,
    enableKeyboardCursorNavigation = false,
    markerMode = false,
    showMarkerPower = false,
    markerTooltipType,
    xAxisDisplayScale,
    xAxisDisplayUnits,
    xAxisDisplayUnitsCode,
    frequencyProfileNativeX = null,
    onNativeXClick,
    cursorLineColor,
    syncCursorValues = null,
    syncMarkerValues = null,
    syncCursorDatasetIndex = null,
    syncMarkerDatasetIndex = null,
    onSyncedCursorAction,
  } = props;
  const xDisplayScale = xAxisDisplayScale ?? 1;
  const getDatasetXDisplayScale = useCallback(
    (datasetIndex: number) => resolveSignalXDisplayScale(data[datasetIndex], xAxisDisplayUnitsCode, xDisplayScale),
    [data, xAxisDisplayUnitsCode, xDisplayScale],
  );

  const useCascadeLayout = data.length <= 1 || displayCascade3D;

  const canvasRef1 = useRef<HTMLCanvasElement | null>(null);
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const chartRef1 = useRef<IChartWithCrosshair | null>(null);
  const { dbTz } = useAsu();
  const { profile } = useUser<IUser>();

  const { formatMessage } = useIntl();
  const { zoomState, applySelectionZoom, clearZoomState, resetZoom, isZoomed, overview, panZoom } = useChartZoom(
    chartRef1,
    {
      registerReset,
      onZoomStateChange,
    },
  );

  const { x0, xOffset, yOffset } = useMemo(() => {
    const xValues = data.flatMap(d => d.items.map(item => item.x));
    const x0 = xValues.length ? xValues.reduce((min, val) => Math.min(min, val), Infinity) : 0;
    const xN = xValues.length ? xValues.reduce((max, val) => Math.max(max, val), -Infinity) : 0;
    const xOffset = data.length > 1 ? (xN - x0) / data.length : 0;

    const yValues = data.flatMap(d => d.items.map(item => (typeof item.y === "number" ? item.y : item.y?.real ?? 0)));
    const y0 = yValues.length ? yValues.reduce((min, val) => Math.min(min, val), Infinity) : 0;
    const yN = yValues.length ? yValues.reduce((max, val) => Math.max(max, val), -Infinity) : 0;
    const yOffset = data.length > 1 ? (yN - y0) / data.length : 0;

    return { x0, xOffset, yOffset };
  }, [data]);

  const chartData = useMemo<ChartData<"line", any[]>>(
    () => ({
      datasets: data.map(({ name, items, color }, i) => {
        const offset = useCascadeLayout ? i * yOffset : 0;

        return {
          type: "line",
          label: name,
          borderColor: color,
          borderWidth: 0.5,
          data: items.map(item => {
            const baseY = typeof item.y === "number" ? item.y : item.y?.real ?? 0;
            return {
              x: item.x + (useCascadeLayout ? i * xOffset : 0),
              y: baseY + offset,
            };
          }),
          pointRadius: 0,
          pointHoverBorderWidth: 8,
        };
      }),
    }),
    [data, xOffset, yOffset, useCascadeLayout],
  );

  const pluginXOffset = useCascadeLayout ? xOffset : 0;
  const pluginYOffset = useCascadeLayout ? yOffset : 0;
  const markerPassageXValuesByDataset = useMemo(() => data.map(getSignalMarkerPassageXValues), [data]);
  const spectraValues = useMemo(
    () => data.map(series => series.items.map(item => (typeof item.y === "number" ? item.y : item.y.real ?? 0))),
    [data],
  );

  const handleHeaderTooltipContentChange = useCallback(
    (content: IExternalTooltipContent | null) => {
      onHeaderTooltipChange?.(content ? formatExternalTooltipHeaderText(content) : null);
    },
    [onHeaderTooltipChange],
  );

  const hadExternalSyncRef = useRef(false);

  const handleNativeXClick = useCallback(
    (chart: IChartWithCrosshair) => {
      const selectionZoom = (
        chart as IChartWithCrosshair & { selectionZoom?: { dragging?: boolean; suppressClickLock?: number } }
      ).selectionZoom;

      if (selectionZoom?.dragging || (selectionZoom?.suppressClickLock && selectionZoom.suppressClickLock > 0)) {
        return;
      }

      const nativeX = chart.crosshair?.baseValue;
      const datasetIndex = chart.crosshair?.baseDatasetIndex ?? 0;
      const locked = Boolean(chart.crosshair?.locked);
      const markerValue = chart.crosshair?.markerValue;
      const markerNativeX = typeof markerValue === "number" && Number.isFinite(markerValue) ? markerValue : null;
      const markerDatasetIndex = chart.crosshair?.markerDatasetIndex ?? null;

      if (typeof nativeX === "number" && Number.isFinite(nativeX)) {
        onNativeXClick?.(nativeX, datasetIndex);
      }

      onSyncedCursorAction?.({
        nativeX: typeof nativeX === "number" && Number.isFinite(nativeX) ? nativeX : null,
        datasetIndex,
        locked,
        markerNativeX,
        markerDatasetIndex,
      });
    },
    [onNativeXClick, onSyncedCursorAction],
  );

  const options = useMemo<ChartOptions<"line"> & { plugins: any }>(
    () => ({
      animation: false,
      maintainAspectRatio: false,
      events: CHART_SELECTION_EVENTS,
      scales: {
        x: applyZoomBounds(
          {
            type: "linear" as const,
            min: x0,
            ticks: {
              maxTicksLimit: 20,
              ...(xAxisDisplayScale == null
                ? {}
                : {
                    callback: (value: string | number) =>
                      formatMeasurementNumber(
                        Number(value) * xDisplayScale,
                        xAxisDisplayUnitsCode ?? null,
                        profile.interfaceSettings.dataFormat,
                        { fallbackDigits: 2 },
                      ),
                  }),
              font: {
                size: 12,
                family: getFontFamily(),
              },
            },
            title: {
              display: true,
              text: xAxisDisplayUnits ?? data[0]?.unitsX,
              font: {
                size: 12,
                family: getFontFamily(),
              },
              color: "rgba(0, 0, 0, 0.87)",
            },
          },
          zoomState?.x,
        ),
        y: applyZoomBounds(
          {
            grace: "10%",
            min: data[0]?.unitsX === formatMessage({ id: `enums.units.3` }) ? 0 : undefined,
            title: {
              display: true,
              text: data[0]?.unitsY,
              font: {
                size: 12,
                family: getFontFamily(),
              },
              color: "rgba(0, 0, 0, 0.87)",
            },
            ticks: {
              font: {
                size: 11,
                family: getFontFamily(),
              },
            },
          },
          zoomState?.y?.y,
        ),
      },
      elements: {
        point: {
          borderWidth: 0.5,
        },
      },
      interaction: {
        intersect: false,
        mode: "nearest" as const,
        axis: "x",
      },
      plugins: {
        drawConnectingLines:
          useCascadeLayout || frequencyProfileNativeX != null
            ? {
                xOffset: pluginXOffset,
                yOffset: pluginYOffset,
                showActiveCascadeLine: showCascadeHoverLine,
                showGridConnections: useCascadeLayout,
                profileNativeX: frequencyProfileNativeX,
                grid:
                  overview?.domain.x && overview.domain.y.y
                    ? {
                        xTicks: overview.scaleTicks.x,
                        yTicks: overview.scaleTicks.y,
                        xMin: overview.domain.x.min,
                        yBase: overview.domain.y.y.min < 0 && overview.domain.y.y.max > 0 ? 0 : overview.domain.y.y.min,
                      }
                    : undefined,
              }
            : false,
        title: {
          display: false,
          text: chartTitle,
          font: {
            size: 10,
            family: getFontFamily(),
          },
        },
        alarmLevel: false,
        htmlLegend: {
          containerId: "legend-container-signal",
          fontFamily: getFontFamily(),
        },
        legend: {
          display: false,
          labels: {
            boxWidth: 10,
            padding: 10,
            font: {
              size: 10,
              family: getFontFamily(),
            },
            generateLabels: (chart: Chart<"line">) => {
              const { data } = chart;
              return data.datasets.map((dataset, i: number) => ({
                text: dataset.label || "",
                fillStyle: dataset.borderColor as Color,
                strokeStyle: dataset.borderColor as Color,
                borderRadius: 5,
                usePointStyle: true,
                pointStyle: "circle" as const,
                lineWidth: chart.isDatasetVisible(i) ? 0 : 1,
                hidden: !chart.isDatasetVisible(i),
                datasetIndex: i,
                onClick: () => {
                  const meta = chart.getDatasetMeta(i);
                  meta.hidden = meta.hidden ?? !chart.data.datasets[i].hidden;
                  chart.update();
                },
              }));
            },
          },
        },
        tooltip: {
          enabled: false,
          intersect: false,
          mode: "nearest" as const,
          external: ExternalTooltip,
          displayMode: tooltipInHeader ? "header" : "floating",
          onHeaderContentChange: tooltipInHeader ? handleHeaderTooltipContentChange : undefined,
          contentDecorator: markerMode
            ? (content: IExternalTooltipContent, dataPoints: TooltipItem<"line">[], chart: IChartWithCrosshair) => {
                const dataPoint = dataPoints[0];
                const datasetIndex = dataPoint?.datasetIndex ?? 0;
                const series = data[datasetIndex];
                const markerValue = chart.crosshair?.syncMarkerValues
                  ? chart.crosshair.syncMarkerValues[datasetIndex]
                  : chart.crosshair?.markerValue;
                const cursorValue = chart.crosshair?.baseValue;
                const parsedY = dataPoint?.parsed?.y;
                if (!series || typeof cursorValue !== "number" || typeof parsedY !== "number") {
                  return content;
                }

                const isSignal = markerTooltipType === "signal";
                const dataFormat = profile.interfaceSettings.dataFormat;
                const formatValue = (value: number, unitsCode: number | null) =>
                  formatMeasurementNumber(value, unitsCode, dataFormat, { fallbackDigits: 2 });
                const xUnits = xAxisDisplayUnits ?? series.unitsX;
                const xUnitsCode = xAxisDisplayUnitsCode ?? series.unitsXCode ?? null;
                const datasetXDisplayScale = getDatasetXDisplayScale(datasetIndex);
                const measurementDate = isSignal ? formatSignalMeasurementDate(series.date, dbTz) : null;
                const title = isSignal
                  ? [series.measurementName, measurementDate].filter(Boolean).join(" ")
                  : series.name;
                const xLabel = formatMessage({
                  id: isSignal
                    ? "pages.multiAnalysis.toolbar.markerRevolutions"
                    : "pages.multiAnalysis.toolbar.markerFrequency",
                });
                const baseContent = buildMarkerTooltipContent({
                  title,
                  x: {
                    label: xLabel,
                    value: formatValue(cursorValue * datasetXDisplayScale, xUnitsCode),
                    units: xUnits,
                  },
                  amplitude: {
                    label: formatMessage({ id: "pages.multiAnalysis.toolbar.markerAmplitude" }),
                    value: formatValue(parsedY - datasetIndex * pluginYOffset, series.unitsYCode ?? null),
                    units: series.unitsY,
                  },
                });

                const bandContent = appendMarkerBandTooltipContent({
                  content: baseContent,
                  markerValue: typeof markerValue === "number" ? markerValue * datasetXDisplayScale : markerValue,
                  cursorValue: typeof cursorValue === "number" ? cursorValue * datasetXDisplayScale : cursorValue,
                  label: formatMessage({ id: "pages.multiAnalysis.toolbar.markerBand" }),
                  units: xAxisDisplayUnits ?? series?.unitsX,
                  formatValue: value =>
                    formatMeasurementNumber(
                      value,
                      xAxisDisplayUnitsCode ?? series?.unitsXCode ?? null,
                      profile.interfaceSettings.dataFormat,
                      {
                        fallbackDigits: 2,
                      },
                    ),
                });

                if (!showMarkerPower || !series || series.items.length < 2) {
                  return bandContent;
                }

                const firstX = series.items[0].x;
                const lastX = series.items[series.items.length - 1].x;
                const power = calcSpectraBandPowerInRange({
                  spectra: spectraValues[datasetIndex],
                  markerX: markerValue,
                  cursorX: cursorValue,
                  firstX,
                  lastX,
                  isHarmonic: series.rangeType === 2,
                });

                return appendMarkerPowerTooltipContent({
                  content: bandContent,
                  power,
                  label: formatMessage({ id: "pages.multiAnalysis.toolbar.markerPower" }),
                  units: series.unitsY,
                  formatValue: value =>
                    formatMeasurementNumber(value, series.unitsYCode ?? null, profile.interfaceSettings.dataFormat, {
                      fallbackDigits: 2,
                    }),
                });
              }
            : undefined,
          unitsX: xAxisDisplayUnits ?? data[0]?.unitsX,
          unitsY: data[0]?.unitsY,
          xOffset: pluginXOffset,
          yOffset: pluginYOffset,
          titleFormatter: (dataPoint: TooltipItem<"line">) =>
            formatSignalCascadeTooltipTitle({
              data,
              datasetIndex: dataPoint.datasetIndex,
              datasetLabel: dataPoint.dataset.label,
              dbTz,
              displayCascade3D,
              shiftedX: dataPoint.parsed.x,
              xOffset: pluginXOffset,
              displayUnitsX: xAxisDisplayUnits,
              formatXValue: (value, datasetIndex) =>
                formatMeasurementNumber(
                  value * getDatasetXDisplayScale(datasetIndex),
                  xAxisDisplayUnitsCode ?? data[datasetIndex]?.unitsXCode ?? null,
                  profile.interfaceSettings.dataFormat,
                  { fallbackDigits: 2 },
                ),
            }),
          xValueFormatter: (value: number, dataPoint: { datasetIndex: number }) =>
            formatMeasurementNumber(
              value * getDatasetXDisplayScale(dataPoint.datasetIndex),
              xAxisDisplayUnitsCode ?? data[dataPoint.datasetIndex]?.unitsXCode ?? null,
              profile.interfaceSettings.dataFormat,
              { fallbackDigits: 2 },
            ),
          yValueFormatter: (value: number, dataPoint: { datasetIndex: number }) =>
            formatMeasurementNumber(
              value,
              data[dataPoint.datasetIndex]?.unitsYCode ?? null,
              profile.interfaceSettings.dataFormat,
              { fallbackDigits: 2 },
            ),
        },
        crosshair: {
          enabled: true,
          onCursorClick: onNativeXClick || onSyncedCursorAction ? handleNativeXClick : undefined,
          line: {
            color: cursorLineColor ?? lineColors[0],
            width: 0.5,
          },
          sync: {
            enabled: false,
          },
          zoom: {
            enabled: false,
          },
          xOffset: pluginXOffset,
          keyboardNavigationEnabled: enableKeyboardCursorNavigation,
          markerMode,
        },
        harmonicCursors: showHarmonicCursors
          ? {
              enabled: true,
              xOffset: pluginXOffset,
            }
          : false,
        subHarmonicCursors: showSubHarmonicCursors
          ? {
              enabled: true,
              xOffset: pluginXOffset,
            }
          : false,
        faultFrequenciesCursors:
          faultFrequencies && faultFrequencies.length
            ? {
                enabled: true,
                cursors: faultFrequencies.map(({ frequencyHz, color }) => ({
                  frequency: frequencyHz,
                  lineColor: color,
                })),
                xOffset: pluginXOffset,
              }
            : false,
        markerPassageLines: showMarkerPoints
          ? {
              enabled: true,
              xValuesByDataset: markerPassageXValuesByDataset,
              xOffset: pluginXOffset,
            }
          : false,
        selectionZoom: {
          enabled: true,
          xScaleId: "x",
          yScaleIds: ["y"],
          minDragPx: 8,
          onZoom: applySelectionZoom,
          onReset: clearZoomState,
        },
      },
    }),
    [
      x0,
      data,
      formatMessage,
      chartTitle,
      spectraValues,
      pluginXOffset,
      pluginYOffset,
      useCascadeLayout,
      showCascadeHoverLine,
      frequencyProfileNativeX,
      showHarmonicCursors,
      showSubHarmonicCursors,
      showMarkerPoints,
      faultFrequencies,
      markerPassageXValuesByDataset,
      zoomState,
      overview,
      applySelectionZoom,
      clearZoomState,
      profile.interfaceSettings.dataFormat,
      tooltipInHeader,
      handleHeaderTooltipContentChange,
      enableKeyboardCursorNavigation,
      markerMode,
      showMarkerPower,
      markerTooltipType,
      dbTz,
      displayCascade3D,
      xAxisDisplayScale,
      xAxisDisplayUnits,
      xAxisDisplayUnitsCode,
      xDisplayScale,
      getDatasetXDisplayScale,
      handleNativeXClick,
      onNativeXClick,
      onSyncedCursorAction,
      cursorLineColor,
    ],
  );

  const renderChart = useCallback(() => {
    if (!canvasRef1.current) return;

    chartRef1.current = new ChartJS<"line">(canvasRef1.current, {
      type: "line",
      data: chartData,
      options,
    });
  }, [chartData, options]);

  const destroyChart = useCallback(() => {
    if (chartRef1.current) {
      hideExternalTooltip(chartRef1.current, { force: true });
      chartRef1.current.destroy();
      chartRef1.current = null;
    }
  }, []);

  const handleResize = useCallback(() => {
    const chart = chartRef1.current;
    const container = chartContainerRef.current;

    if (!chart || !container) {
      return;
    }

    const width = Math.floor(container.clientWidth);
    const height = Math.floor(container.clientHeight);

    if (width <= 0 || height <= 0 || (chart.width === width && chart.height === height)) {
      return;
    }

    chart.resize(width, height);
  }, []);

  const handleChartAreaMouseLeave = useCallback(() => {
    const chart = chartRef1.current;
    if (chart?.crosshair?.locked) {
      return;
    }

    hideExternalTooltip(chart);
  }, []);

  const exportChart = useCallback(
    (format: "image/png" | "image/jpeg", fileName: string, width: number) => {
      if (chartRef1.current && canvasRef1.current) {
        const dpr = window.devicePixelRatio || 1;
        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = width / dpr;
        tempCanvas.height = (width * (canvasRef1.current.height / canvasRef1.current.width)) / dpr;
        const tempCtx = tempCanvas.getContext("2d");

        if (tempCtx) {
          const tempChart = new ChartJS(tempCanvas, {
            type: "line",
            data: chartData,
            options: {
              ...options,
              maintainAspectRatio: false,
              responsive: false,
              plugins: {
                ...options.plugins,
                title: {
                  ...options.plugins.title,
                  display: true,
                },
                legend: {
                  ...options.plugins.legend,
                  display: true,
                },
              },
            },
          });

          if (format === "image/jpeg") {
            tempCtx.save();
            tempCtx.globalCompositeOperation = "destination-over";
            tempCtx.fillStyle = "white";
            tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
            tempCtx.restore();
          }

          const dataUrl = tempChart.toBase64Image(format, 1);
          downloadDiagram(dataUrl, fileName);

          tempChart.destroy();
          tempCanvas.remove();
        }
      }
    },
    [chartData, options],
  );

  useEffect(() => {
    renderChart();
    return destroyChart;
  }, [renderChart, destroyChart]);

  useEffect(() => {
    const chart = chartRef1.current;

    if (!chart) {
      return;
    }

    const hasExternalSync = syncCursorValues != null || syncMarkerValues != null;

    if (!hasExternalSync) {
      if (hadExternalSyncRef.current) {
        clearSyncedCrosshair(chart);
        hideExternalTooltip(chart, { force: true });
        onHeaderTooltipChange?.(null);
        hadExternalSyncRef.current = false;
      }

      return;
    }

    hadExternalSyncRef.current = true;

    const hasCursorPoint = applySyncedCrosshair(chart, {
      cursorValues: syncCursorValues,
      markerValues: markerMode ? syncMarkerValues : null,
      cursorDatasetIndex: syncCursorDatasetIndex,
      markerDatasetIndex: syncMarkerDatasetIndex,
    });

    if (!hasCursorPoint) {
      hideExternalTooltip(chart, { force: true });
      onHeaderTooltipChange?.(null);
    }
  }, [
    chartData,
    onHeaderTooltipChange,
    options,
    syncCursorValues,
    syncMarkerValues,
    markerMode,
    syncCursorDatasetIndex,
    syncMarkerDatasetIndex,
  ]);

  useEffect(() => {
    const observer = new ResizeObserver(handleResize);

    if (chartContainerRef.current) {
      observer.observe(chartContainerRef.current);
    }

    handleResize();

    return () => {
      observer.disconnect();
    };
  }, [handleResize, data]);

  useEffect(() => {
    resetZoom();
  }, [data, displayCascade3D, resetZoom]);

  useEffect(() => {
    registerExport?.(format => {
      const fileName = getChartExportFileName(format);
      exportChart(format === "png" ? "image/png" : "image/jpeg", fileName, 1024);
    });

    return () => {
      registerExport?.(null);
    };
  }, [exportChart, registerExport]);

  useEffect(() => {
    const canvas = canvasRef1.current;
    if (!canvas) return;

    const handleMiddleClick = (event: MouseEvent) => {
      if (event.button === 1 && isZoomed) {
        event.preventDefault();
        event.stopImmediatePropagation();
        resetZoom();
      }
    };

    canvas.addEventListener("mousedown", handleMiddleClick, true);
    canvas.addEventListener("auxclick", handleMiddleClick, true);
    return () => {
      canvas.removeEventListener("mousedown", handleMiddleClick, true);
      canvas.removeEventListener("auxclick", handleMiddleClick, true);
    };
  }, [isZoomed, resetZoom]);

  return (
    <StyledWrapper onMouseLeave={handleChartAreaMouseLeave}>
      <StyledChartContainer ref={chartContainerRef}>
        <ChartZoomOverview overview={overview} zoomState={zoomState} onPan={panZoom} />
        <canvas
          ref={canvasRef1}
          role="img"
          style={{
            display: "block",
            width: "100%",
            height: "100%",
            cursor: markerMode || onNativeXClick || onSyncedCursorAction ? "crosshair" : undefined,
          }}
        />
      </StyledChartContainer>
    </StyledWrapper>
  );
};
